import React from "react";
import {
  Banner,
  BlockStack,
  Button,
  Icon,
  InlineGrid,
  Text,
} from "@shopify/polaris";
import {
  BookOpenIcon,
  CashDollarIcon,
  ClipboardChecklistIcon,
  ConnectIcon,
  ProductListIcon,
  SettingsIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  GlobeIcon,
  RefreshIcon,
} from "@shopify/polaris-icons";
import { usePlan } from "./context/PlanContext";
import { buildEmbeddedPath } from "./utils/shopifyApp";

const setupSteps = [
  {
    id: "plan",
    badge: "Required",
    title: "Activate a plan",
    icon: CashDollarIcon,
    body:
      "Choose a billing plan first. The rest of the app opens after the store has an active plan.",
    details: [
      "Plan access controls premium areas such as Product Check, Additional Countries, and Metafield Re-Sync.",
      "If the app only shows Plan and How to Use, billing has not been activated yet.",
    ],
    action: { label: "Open Plan", url: "/plan" },
  },
  {
    id: "google",
    badge: "Required",
    title: "Connect Google Merchant Center",
    icon: ConnectIcon,
    body:
      "Authorize Google, then select the Merchant Center account that should receive the product feed.",
    details: [
      "Use the Google account that has access to the correct Merchant Center account.",
      "After connecting, confirm the selected Merchant ID before syncing products.",
    ],
    action: { label: "Open Integrations", url: "/integrations" },
  },
  {
    id: "settings",
    badge: "Required",
    title: "Configure feed settings",
    icon: SettingsIcon,
    body:
      "Set the store defaults that become part of each Google product submission.",
    details: [
      "Review target country, content language, product condition, age group, gender, and category defaults.",
      "Choose how variants are submitted, whether SEO title/description should be used, and whether extra images should be included.",
      "Add feed rules when product values need to be copied or transformed before upload.",
    ],
    action: { label: "Open Settings", url: "/tab-settings" },
  },
  {
    id: "validation",
    badge: "Recommended",
    title: "Check product readiness",
    icon: ClipboardChecklistIcon,
    body:
      "Review products before submitting so missing images, descriptions, prices, or policy-sensitive values do not reach Google by accident.",
    details: [
      "Product Check can enforce quality rules before upload when it is included in the current plan.",
      "Products with missing required data move to Ineligible instead of being submitted.",
      "Warnings help you catch GTIN, brand, and shipping setup gaps early.",
    ],
    action: {
      label: "Open Product Check",
      url: "/product-check",
      feature: "product_check",
    },
  },
  {
    id: "sync",
    badge: "Daily workflow",
    title: "Sync and monitor products",
    icon: ProductListIcon,
    body:
      "Submit selected products or sync the full catalog, then review Google responses and diagnostics.",
    details: [
      "Accepted products are tracked in Submitted. Failed products move to Ineligible with the Google error message.",
      "Google may take several minutes to process accepted product inputs before they appear in Merchant Center.",
      "Use Resync after fixing Shopify data, app defaults, feed rules, or Google account issues.",
    ],
    action: { label: "Open Products", url: "/products" },
  },
];

const workflowSections = [
  {
    title: "Before first sync",
    icon: ClockIcon,
    tone: "blue",
    items: [
      "Confirm the correct Merchant Center account is connected.",
      "Set target country and language in Google settings.",
      "Review Shopify store settings, default attributes, sync preferences, and scheduled sync settings.",
      "Configure feed rules only for values that should be changed before upload.",
    ],
  },
  {
    title: "While managing products",
    icon: ProductListIcon,
    tone: "",
    items: [
      "Use Manage Products to view store products, submitted products, ineligible products, and excluded products.",
      "Open a product detail page when you need to edit product labels, custom attributes, promotions, characteristics, or channel submission controls.",
      "Sync one product while testing settings, then sync more products after the first item is accepted.",
      "Use Exclude when a Shopify product should not be sent to Google.",
    ],
  },
  {
    title: "After syncing",
    icon: RefreshIcon,
    tone: "success",
    items: [
      "Read the sync banner carefully. It shows how many products Google accepted and how many failed.",
      "Check Google diagnostics in the product table for disapprovals and item-level issues.",
      "If Google accepted the item, wait a few minutes before expecting it in Merchant Center.",
      "Search Merchant Center by the Shopify variant ID, which is sent as the Google offer ID.",
    ],
  },
];

const featureGuides = [
  {
    feature: null,
    label: "Google connection",
    location: "Integrations",
    icon: ConnectIcon,
    description:
      "Connect OAuth, refresh Google access, choose the Merchant Center account, and fetch account/product diagnostics.",
  },
  {
    feature: null,
    label: "Feed settings",
    location: "Settings",
    icon: SettingsIcon,
    description:
      "Control target market, language, variant submission, title/description source, additional images, scheduled sync, defaults, and feed rules.",
  },
  {
    feature: null,
    label: "Product management",
    location: "Manage Products",
    icon: ProductListIcon,
    description:
      "Sync, resync, exclude, inspect warnings/errors, and edit product-level feed data before sending it to Google.",
  },
  {
    feature: "product_check",
    label: "Product Check",
    location: "Product Check",
    icon: ClipboardChecklistIcon,
    description:
      "Add validation rules that catch product issues before upload, such as missing values or blocked terms.",
  },
  {
    feature: "multi_country",
    label: "Additional Countries",
    location: "Additional Countries",
    icon: GlobeIcon,
    description:
      "Submit products for more target countries after the primary market is configured.",
  },
  {
    feature: "metafield_sync",
    label: "Metafield Re-Sync",
    location: "Settings",
    icon: RefreshIcon,
    description:
      "Refresh Shopify metafield values so the app has the latest product data for feed decisions.",
  },
];

const troubleshootingItems = [
  {
    title: "Product says synced but is not visible in Merchant Center",
    body:
      "A sync success means Google accepted the product input. Merchant Center can take several minutes to process and display it. If the banner shows failures, fix those errors and resync.",
  },
  {
    title: "Google returns invalid field or enum errors",
    body:
      "The app normalizes current Merchant API fields for availability, condition, and diagnostics. If a new Google error appears, use the exact message to update the mapped field.",
  },
  {
    title: "Product is in Ineligible",
    body:
      "The app blocks submission when required data is missing, such as image, description, or price. Open the Shopify product or app product detail, fix the data, then resync.",
  },
  {
    title: "Wrong Merchant Center account",
    body:
      "Reconnect Google or choose the correct Merchant ID before syncing again. Products are submitted only to the selected Merchant Center account.",
  },
];

export default function HowToUse() {
  const { hasPlan, activePlan, hasFeature } = usePlan();

  const canUseFeature = (feature) =>
    !feature || (hasPlan !== false && hasFeature(feature));

  return (
    <div>
      <div className="app-page-hero">
        <div>
          <div className="app-page-hero__eyebrow">
            <Icon source={BookOpenIcon} tone="base" />
            Guide
          </div>
          <h1 className="app-page-hero__title">How to use</h1>
          <p className="app-page-hero__subtitle">
            Set up the Google Merchant feed, submit products, and keep
            diagnostics under control.
          </p>
        </div>
        <div className="app-page-hero__meta">
          <span className="app-chip app-chip--info">
            {setupSteps.length} setup steps
          </span>
          {activePlan ? (
            <span className="app-chip app-chip--success">
              Plan: {activePlan.heading}
            </span>
          ) : null}
        </div>
      </div>

      <BlockStack gap="500">
        {hasPlan === false ? (
          <Banner tone="warning">
            Activate a plan first. Until billing is active, only Plan and How to
            Use remain available.
          </Banner>
        ) : activePlan ? (
          <Banner tone="success">
            Current plan: <strong>{activePlan.heading}</strong>. Follow the
            steps below in order for the cleanest setup.
          </Banner>
        ) : null}

        <div>
          <h2 style={{ margin: "0 0 6px", fontSize: 20 }}>Setup sequence</h2>
          <p style={{ margin: "0 0 14px", color: "var(--app-muted)" }}>
            Complete these steps from top to bottom before running a full
            catalog sync.
          </p>
          <InlineGrid columns={{ xs: 1, sm: 1, md: 2, lg: 3 }} gap="400">
            {setupSteps.map((step, index) => {
              const enabled = canUseFeature(step.action.feature);

              return (
                <div key={step.id} className="howto-step">
                  <div className="howto-step__top">
                    <span className="howto-step__index">{index + 1}</span>
                    <span
                      className={`app-chip${
                        enabled ? "" : " app-chip--warn"
                      }`}
                    >
                      {enabled ? step.badge : "Plan upgrade"}
                    </span>
                  </div>
                  <div className="howto-step__body">
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        marginBottom: 10,
                      }}
                    >
                      <span className="app-icon-tile">
                        <Icon source={step.icon} tone="base" />
                      </span>
                      <Text variant="headingMd" as="h3">
                        {step.title}
                      </Text>
                    </div>
                    <Text as="p" tone="subdued">
                      {step.body}
                    </Text>
                    <ul className="howto-step__list">
                      {step.details.map((detail) => (
                        <li key={detail}>{detail}</li>
                      ))}
                    </ul>
                    <div style={{ marginTop: 16 }}>
                      <Button
                        url={buildEmbeddedPath(step.action.url)}
                        disabled={!enabled}
                        primary={index === 0}
                      >
                        {step.action.label}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </InlineGrid>
        </div>

        <InlineGrid columns={{ xs: 1, md: 3 }} gap="400">
          {workflowSections.map((section) => (
            <div key={section.title} className="app-surface-card">
              <div className="app-surface-card__body">
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    marginBottom: 12,
                  }}
                >
                  <span
                    className={`app-icon-tile${
                      section.tone === "blue"
                        ? " app-icon-tile--blue"
                        : section.tone === "success"
                          ? " app-icon-tile--success"
                          : ""
                    }`}
                  >
                    <Icon source={section.icon} tone="base" />
                  </span>
                  <Text variant="headingMd" as="h2">
                    {section.title}
                  </Text>
                </div>
                <ul className="howto-step__list">
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </InlineGrid>

        <div className="app-surface-card">
          <div className="app-surface-card__body">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 16,
              }}
            >
              <div>
                <h2 style={{ margin: 0, fontSize: 20 }}>What each area does</h2>
                <p style={{ margin: "6px 0 0", color: "var(--app-muted)" }}>
                  Use this as a quick map when deciding where to make a change.
                </p>
              </div>
              {activePlan ? (
                <span className="app-chip">{activePlan.heading}</span>
              ) : null}
            </div>
            <BlockStack gap="300">
              {featureGuides.map((item) => {
                const enabled = canUseFeature(item.feature);
                return (
                  <div
                    key={item.label}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 16,
                      flexWrap: "wrap",
                      padding: "12px 0",
                      borderBottom: "1px solid #edf1f5",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 12,
                        alignItems: "flex-start",
                        flex: 1,
                      }}
                    >
                      <span className="app-icon-tile app-icon-tile--blue">
                        <Icon source={item.icon} tone="base" />
                      </span>
                      <div>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <Text variant="headingMd" as="h3">
                            {item.label}
                          </Text>
                          <span className="app-chip">{item.location}</span>
                        </div>
                        <Text as="p" tone="subdued">
                          {item.description}
                        </Text>
                      </div>
                    </div>
                    <span
                      className={`app-chip${
                        enabled ? " app-chip--success" : " app-chip--warn"
                      }`}
                    >
                      <Icon
                        source={enabled ? CheckCircleIcon : AlertTriangleIcon}
                        tone={enabled ? "success" : "caution"}
                      />
                      {enabled ? "Available" : "Not in current plan"}
                    </span>
                  </div>
                );
              })}
            </BlockStack>
          </div>
        </div>

        <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
          <div className="app-surface-card">
            <div className="app-surface-card__body">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <span className="app-icon-tile">
                  <Icon source={ProductListIcon} tone="base" />
                </span>
                <Text variant="headingLg" as="h2">
                  Product ID behavior
                </Text>
              </div>
              <Text as="p" tone="subdued">
                Google receives the Shopify variant ID as the offer ID. That
                makes each variant trackable in Merchant Center and lets the app
                match Google diagnostics back to Shopify variants.
              </Text>
              <ul className="howto-step__list">
                <li>Shopify variant ID becomes Google offer ID.</li>
                <li>
                  Parent Shopify product ID stays in the app for local feed
                  status tracking.
                </li>
                <li>
                  Use the variant ID when searching for a freshly synced item in
                  Merchant Center.
                </li>
              </ul>
            </div>
          </div>

          <div className="app-surface-card">
            <div className="app-surface-card__body">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  marginBottom: 12,
                }}
              >
                <span className="app-icon-tile app-icon-tile--warn">
                  <Icon source={AlertTriangleIcon} tone="base" />
                </span>
                <Text variant="headingLg" as="h2">
                  Troubleshooting
                </Text>
              </div>
              <BlockStack gap="300">
                {troubleshootingItems.map((item) => (
                  <BlockStack key={item.title} gap="100">
                    <Text variant="headingSm" as="h3">
                      {item.title}
                    </Text>
                    <Text as="p" tone="subdued">
                      {item.body}
                    </Text>
                  </BlockStack>
                ))}
              </BlockStack>
            </div>
          </div>
        </InlineGrid>

        <div className="app-surface-card">
          <div className="app-surface-card__body">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 14,
              }}
            >
              <span className="app-icon-tile app-icon-tile--success">
                <Icon source={CheckCircleIcon} tone="base" />
              </span>
              <Text variant="headingLg" as="h2">
                Healthy feed checklist
              </Text>
            </div>
            <InlineGrid columns={{ xs: 1, md: 2 }} gap="300">
              <ul className="howto-step__list">
                <li>Use HTTPS image URLs for every product.</li>
                <li>Keep descriptions and prices populated in Shopify.</li>
                <li>
                  Set brand, GTIN/barcode, and shipping wherever possible.
                </li>
                <li>Test one product before syncing many products.</li>
              </ul>
              <ul className="howto-step__list">
                <li>Review the sync banner after every upload.</li>
                <li>Fix Ineligible products before resyncing.</li>
                <li>Use diagnostics to catch Google disapprovals.</li>
                <li>
                  Wait a few minutes for accepted products to appear in Merchant
                  Center.
                </li>
              </ul>
            </InlineGrid>
          </div>
        </div>
      </BlockStack>
    </div>
  );
}
